import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import useFormState from '../../hooks/useFormState.js';

const EMPTY_VESSEL = { id: null, name: '', imo: '', mmsi: '', grossTonnage: '', loaM: '', beamM: '' };

export default function FavoriteVesselsPage() {
  const [vessels, setVessels] = useState([]);
  const [options, setOptions] = useState({ ports: [], berths: [] });
  const [prefs, setPrefs] = useState({
    defaultPortId: '',
    defaultBerthId: '',
    defaultPilotageRequired: true,
    notificationEmail: '',
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const { form, setForm, errors, setErrors, setField } = useFormState(EMPTY_VESSEL);
  const [saving, setSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const load = useCallback(async () => {
    const [vs, opts, pf] = await Promise.all([
      api.listFavoriteVessels(),
      api.getProfileOptions(),
      api.getPreferences(),
    ]);
    setVessels(vs || []);
    setOptions(opts || { ports: [], berths: [] });
    setPrefs({
      defaultPortId: pf?.defaultPortId || '',
      defaultBerthId: pf?.defaultBerthId || '',
      defaultPilotageRequired: pf?.defaultPilotageRequired ?? true,
      notificationEmail: pf?.notificationEmail || '',
    });
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load your profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const openAdd = () => {
    setErrors({});
    setForm(EMPTY_VESSEL);
    setModalOpen(true);
  };
  const openEdit = (v) => {
    setErrors({});
    setForm({
      id: v.id,
      name: v.name,
      imo: v.imo,
      mmsi: v.mmsi,
      grossTonnage: String(v.grossTonnage ?? ''),
      loaM: String(v.loaM ?? ''),
      beamM: String(v.beamM ?? ''),
    });
    setModalOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Vessel name is required';
    if (!/^\d{7}$/.test(form.imo)) e.imo = 'IMO must be 7 digits';
    if (!/^\d{9}$/.test(form.mmsi)) e.mmsi = 'MMSI must be 9 digits';
    if (!(Number(form.grossTonnage) > 0)) e.grossTonnage = 'GT must be positive';
    if (!(Number(form.loaM) > 0)) e.loaM = 'LOA must be positive';
    if (!(Number(form.beamM) > 0)) e.beamM = 'Beam must be positive';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveVessel = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        imo: form.imo,
        mmsi: form.mmsi,
        grossTonnage: Number(form.grossTonnage),
        loaM: Number(form.loaM),
        beamM: Number(form.beamM),
      };
      if (form.id) await api.updateFavoriteVessel(form.id, payload);
      else await api.addFavoriteVessel(payload);
      setPageMsg({ type: 'success', text: 'Favorite vessel saved — available in your booking form.' });
      await load();
      setModalOpen(false);
    } catch (err) {
      // 8900: a duplicate IMO/MMSI comes back per field — flag the input that clashes.
      const fieldErrs = Object.fromEntries(
        (err.details || []).filter((d) => d.path).map((d) => [d.path, d.message])
      );
      setErrors((p) => ({ ...p, ...fieldErrs, form: err.message || 'Failed to save vessel.' }));
    } finally {
      setSaving(false);
    }
  };

  const removeVessel = async (v) => {
    setPageMsg(null);
    try {
      await api.deleteFavoriteVessel(v.id);
      await load();
      setPageMsg({ type: 'success', text: `${v.name} removed from favorites.` });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to remove vessel.' });
    }
  };

  const savePrefs = async () => {
    setSavingPrefs(true);
    setPageMsg(null);
    try {
      await api.savePreferences({
        defaultPortId: prefs.defaultPortId || null,
        defaultBerthId: prefs.defaultBerthId || null,
        defaultPilotageRequired: prefs.defaultPilotageRequired,
        notificationEmail: prefs.notificationEmail.trim() || null,
      });
      setPageMsg({ type: 'success', text: 'Preferences saved — defaults will pre-fill your booking form.' });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to save preferences.' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const berthsForPort = options.berths.filter((b) => !prefs.defaultPortId || b.port_id === prefs.defaultPortId);

  return (
    <div className="container-fluid py-4" data-testid="favorite-vessels-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-star me-2" aria-hidden="true" />
                Favorite Vessels &amp; Preferences
              </h3>
              <p className="text-muted mb-0 small">
                Register your standard fleet and booking defaults to accelerate future bookings.
              </p>
            </div>
            <button className="btn btn-primary" onClick={openAdd} data-testid="add-favorite-vessel">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add Favorite Vessel
            </button>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="favorites-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="favorites-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* Favorite vessels list */}
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-ship me-2" aria-hidden="true" />
                  Registered Favorite Vessels
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Name</th>
                        <th>IMO</th>
                        <th>MMSI</th>
                        <th>GT</th>
                        <th>LOA</th>
                        <th>Beam</th>
                        <th aria-label="action" />
                      </tr>
                    </thead>
                    <tbody>
                      {vessels.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4" data-testid="no-favorites">
                            No favorite vessels yet. Add one to speed up bookings.
                          </td>
                        </tr>
                      )}
                      {vessels.map((v) => (
                        <tr key={v.id} data-testid="favorite-row" data-name={v.name} data-imo={v.imo}>
                          <td className="fw-bold">{v.name}</td>
                          <td>
                            <code>{v.imo}</code>
                          </td>
                          <td className="small">{v.mmsi}</td>
                          <td>{v.grossTonnage}</td>
                          <td>{v.loaM} m</td>
                          <td>{v.beamM} m</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => openEdit(v)}
                              data-testid="edit-favorite"
                              aria-label={`Edit ${v.name}`}
                            >
                              <i className="fas fa-pen" aria-hidden="true" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeVessel(v)}
                              data-testid="delete-favorite"
                              aria-label={`Delete ${v.name}`}
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Portal default settings */}
          <div className="col-lg-4 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-sliders-h me-2" aria-hidden="true" />
                  Portal Default Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="pref-port" className="form-label small fw-bold text-muted">
                    Default Port
                  </label>
                  <select
                    id="pref-port"
                    className="form-select"
                    value={prefs.defaultPortId}
                    onChange={(e) => setPrefs({ ...prefs, defaultPortId: e.target.value, defaultBerthId: '' })}
                    data-testid="pref-port"
                  >
                    <option value="">No default</option>
                    {options.ports.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="pref-berth" className="form-label small fw-bold text-muted">
                    Default Berth
                  </label>
                  <select
                    id="pref-berth"
                    className="form-select"
                    value={prefs.defaultBerthId}
                    onChange={(e) => setPrefs({ ...prefs, defaultBerthId: e.target.value })}
                    data-testid="pref-berth"
                  >
                    <option value="">No default</option>
                    {berthsForPort.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="pref-pilotage"
                    checked={prefs.defaultPilotageRequired}
                    onChange={(e) => setPrefs({ ...prefs, defaultPilotageRequired: e.target.checked })}
                    data-testid="pref-pilotage"
                  />
                  <label className="form-check-label small" htmlFor="pref-pilotage">
                    Pilotage required by default
                  </label>
                </div>
                <div className="mb-3">
                  <label htmlFor="pref-email" className="form-label small fw-bold text-muted">
                    Contact Email for Notifications
                  </label>
                  <input
                    id="pref-email"
                    type="email"
                    className="form-control"
                    value={prefs.notificationEmail}
                    onChange={(e) => setPrefs({ ...prefs, notificationEmail: e.target.value })}
                    data-testid="pref-email"
                  />
                </div>
                <button
                  className="btn btn-primary w-100"
                  onClick={savePrefs}
                  disabled={savingPrefs}
                  data-testid="save-preferences"
                >
                  {savingPrefs && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / edit vessel modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="vessel-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={saveVessel} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-ship me-2 text-primary" aria-hidden="true" />
                      {form.id ? 'Edit Favorite Vessel' : 'Add Favorite Vessel'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && <div className="alert alert-danger py-2">{errors.form}</div>}
                    <div className="mb-3">
                      <label htmlFor="v-name" className="form-label small fw-bold text-muted">
                        Vessel Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="v-name"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={form.name}
                        onChange={(e) => setField('name', e.target.value)}
                        data-testid="vessel-name"
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label htmlFor="v-imo" className="form-label small fw-bold text-muted">
                          IMO <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-imo"
                          className={`form-control ${errors.imo ? 'is-invalid' : ''}`}
                          value={form.imo}
                          onChange={(e) => setField('imo', e.target.value)}
                          data-testid="vessel-imo"
                        />
                        {errors.imo && <div className="invalid-feedback">{errors.imo}</div>}
                      </div>
                      <div className="col-6 mb-3">
                        <label htmlFor="v-mmsi" className="form-label small fw-bold text-muted">
                          MMSI <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-mmsi"
                          className={`form-control ${errors.mmsi ? 'is-invalid' : ''}`}
                          value={form.mmsi}
                          onChange={(e) => setField('mmsi', e.target.value)}
                          data-testid="vessel-mmsi"
                        />
                        {errors.mmsi && <div className="invalid-feedback">{errors.mmsi}</div>}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-4 mb-3">
                        <label htmlFor="v-gt" className="form-label small fw-bold text-muted">
                          GT <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-gt"
                          type="number"
                          className={`form-control ${errors.grossTonnage ? 'is-invalid' : ''}`}
                          value={form.grossTonnage}
                          onChange={(e) => setField('grossTonnage', e.target.value)}
                          data-testid="vessel-gt"
                        />
                        {errors.grossTonnage && <div className="invalid-feedback">{errors.grossTonnage}</div>}
                      </div>
                      <div className="col-4 mb-3">
                        <label htmlFor="v-loa" className="form-label small fw-bold text-muted">
                          LOA (m) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-loa"
                          type="number"
                          className={`form-control ${errors.loaM ? 'is-invalid' : ''}`}
                          value={form.loaM}
                          onChange={(e) => setField('loaM', e.target.value)}
                          data-testid="vessel-loa"
                        />
                        {errors.loaM && <div className="invalid-feedback">{errors.loaM}</div>}
                      </div>
                      <div className="col-4 mb-3">
                        <label htmlFor="v-beam" className="form-label small fw-bold text-muted">
                          Beam (m) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="v-beam"
                          type="number"
                          className={`form-control ${errors.beamM ? 'is-invalid' : ''}`}
                          value={form.beamM}
                          onChange={(e) => setField('beamM', e.target.value)}
                          data-testid="vessel-beam"
                        />
                        {errors.beamM && <div className="invalid-feedback">{errors.beamM}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-vessel">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      Save Vessel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
